import type { MessageEvent } from '@shared/index'
import { localizedName } from './localizeCard'
import { t } from './t'

/**
 * English engine sentences → `duel.error.*`. Used when a payload has no
 * `code` (an older server). New payloads translate by code first.
 */
const ENGINE_ERRORS: Array<[string, string]> = [
  ['Waiting for a trap response', 'waitingTrapResponse'],
  ['You must discard down to the hand limit first', 'discardFirst'],
  ['Waiting for a Flip Effect target selection', 'waitingFlipTarget'],
  ['Waiting for a target selection', 'waitingTarget'],
  ['Waiting for Ultimate Offering', 'waitingOffering'],
  ['Normal Summon or Set, or pass', 'offeringSummonOrPass'],
  ['Ultimate Offering: Normal Summon or Set the monster you paid for', 'offeringSummonOwed'],
  ['Not your turn', 'notYourTurn'],
  ['Not in a Main Phase', 'notMainPhase'],
  ['Not in the Battle Phase', 'notBattlePhase'],
  ['Not in the Battle Step', 'notBattleStep'],
  ['An attack is still resolving', 'attackResolving'],
  ['You can only skip ahead to the End Phase', 'skipEndOnly'],
  ['Cannot skip to the End Phase from here', 'cannotSkipEnd'],
  ['Card not in hand', 'cardNotInHand'],
  ['Not a monster', 'notMonster'],
  ['Fusion monsters cannot be Normal Summoned', 'fusionNoNormal'],
  ['This monster cannot be Normal Summoned or Set', 'noNormalSummon'],
  ['Requires tribute summon', 'requiresTribute'],
  ['Monster zone occupied', 'monsterZoneOccupied'],
  ['Invalid zone', 'invalidZone'],
  ['Fusion monsters cannot be Tribute Summoned', 'fusionNoTribute'],
  ['Does not require tributes', 'noTributesNeeded'],
  ['Invalid tribute', 'invalidTribute'],
  ['No free monster zone', 'noFreeMonsterZone'],
  ['Monster not found', 'monsterNotFound'],
  ['Cannot change position the turn it was summoned', 'noPositionSummonTurn'],
  ['Position already changed this turn', 'positionAlreadyChanged'],
  ['Monster already attacked', 'monsterAlreadyAttacked'],
  ['Dragons are locked in Defense Position (Dragon Capture Jar)', 'dragonCaptureJar'],
  ['Already face-up', 'alreadyFaceUp'],
  ['Cannot flip the turn it was set', 'noFlipSetTurn'],
  ['Not a spell/trap', 'notSpellTrap'],
  ['No free spell/trap zone', 'noFreeSpellZone'],
  ['Not a spell', 'notSpell'],
  ['Set spell not found', 'setSpellNotFound'],
  ['Set trap not found', 'setTrapNotFound'],
  ['Not a trap', 'notTrap'],
  ['A trap cannot be activated the turn it was set', 'trapSetDelay'],
  ['This card cannot be activated in the Damage Step', 'noDamageStep'],
  ['This card cannot be activated right now', 'cannotActivateNow'],
  ['Activation conditions are not met', 'conditionsNotMet'],
  ['No legal target for this card', 'noLegalTarget'],
  ['Select valid targets for this card', 'selectValidTargets'],
  ['Select a valid target for this card', 'selectValidTarget'],
  ['No flip selection pending', 'noFlipPending'],
  ['Not your selection', 'notYourSelection'],
  ['Invalid target', 'invalidTarget'],
  ['No target selection pending', 'noTargetPending'],
  ['Attacker not found', 'attackerNotFound'],
  ['Target not found', 'targetNotFound'],
  ['Opponent controls monsters; cannot attack directly', 'cannotDirectAttack'],
  ['Attacker is face-down', 'attackerFaceDown'],
  ['Attacker not in attack position', 'attackerNotAttackPos'],
  ['Already attacked this turn', 'alreadyAttacked'],
  ['Cannot attack on the very first turn', 'noFirstTurnAttack'],
  ['You cannot attack (Swords of Revealing Light)', 'swordsOfRevealingLight'],
  ['Target is not a monster', 'targetNotMonster'],
  ['No pending response', 'noPendingResponse'],
  ['It is not your response to make', 'notYourResponse'],
  ['That card cannot be activated now', 'cardCannotActivateNow'],
  ['Chain is too long', 'chainTooLong'],
  ['Cannot chain a slower effect', 'cannotChainSlower'],
  ['Spell Speed 1 cannot be chained', 'spellSpeed1NoChain'],
  ['You do not owe a discard', 'noDiscardOwed'],
  ['That card is not in your hand', 'cardNotYourHand'],
  ['Normal summon already used this turn', 'normalSummonUsed'],
  ["Ultimate Offering: your Main Phase or the opponent's Battle Phase", 'offeringPhase'],
  ['Not enough Life Points', 'notEnoughLp'],
  ['No monster you can Normal Summon or Tribute Summon', 'noOfferingMonster'],
  ['Match not found', 'matchNotFound'],
  ['You are not in this match', 'notInMatch'],
  ['Invalid action', 'invalidAction'],
  ['Internal error', 'internalError'],
]

const ENGINE_ERROR_KEYS: Record<string, string> = Object.fromEntries(
  ENGINE_ERRORS.map(([message, code]) => [message, `duel.error.${code}`]),
)

const KIND_KEYS: Record<string, string> = {
  trap: 'duel.msg.kindTrap',
  spell: 'duel.msg.kindSpell',
}

const ATTACK_CANCEL_KEYS: Record<string, string> = {
  attackerGone: 'duel.msg.attackCancelAttacker',
  targetGone: 'duel.msg.attackCancelTarget',
  oppHasMonster: 'duel.msg.attackCancelOpp',
}

function translated(
  key: string,
  vars?: Record<string, string | number>,
): string | undefined {
  const hit = t(key, vars)
  return hit === key ? undefined : hit
}

/**
 * These rulings are true, but the wording names the Battle substep or a trap
 * window. Shown while the opponent is choosing, that classifies a face-down
 * card. Card-specific failures ("cannot be activated in the Damage Step")
 * stay as written: only the player who tried the card receives them.
 *
 * When the other player already owes that choice, the toast says so. It still
 * does not name the substep or the Set card. `notNow` is the client's own
 * code for the same pause.
 */
const HIDDEN_TIMING_CODES = new Set([
  'notNow',
  'notBattleStep',
  'attackResolving',
  'waitingTrapResponse',
  'notYourResponse',
])

const HIDDEN_TIMING_MESSAGES = new Set([
  'Not in the Battle Step',
  'An attack is still resolving',
  'Waiting for a trap response',
  'It is not your response to make',
])

export function localizeEngineError(
  message: string,
  code?: string,
  vars?: Record<string, string | number>,
  waiting = false,
): string {
  if (!message && !code) return message
  if (
    (code && HIDDEN_TIMING_CODES.has(code)) ||
    HIDDEN_TIMING_MESSAGES.has(message)
  ) {
    return t(waiting ? 'duel.error.notNowWaiting' : 'duel.error.notNow')
  }
  if (code) {
    const hit = translated(`duel.error.${code}`, vars)
    if (hit) return hit
  }
  const key = ENGINE_ERROR_KEYS[message]
  if (key) return t(key, vars)
  const tributes = /^Needs exactly (\d+) tributes$/.exec(message)
  if (tributes) {
    return t('duel.error.needsExactTributes', { n: Number(tributes[1]) })
  }
  return message
}

/**
 * Translate an `EventType.MESSAGE`. Card passcodes in `vars.cardId` become
 * the locale's card name. Without a code, the English `text` is returned.
 */
export function localizeDuelMessage(event: MessageEvent): string {
  if (!event.code) return event.text
  const vars: Record<string, string | number> = { ...(event.vars ?? {}) }
  if (vars.cardId !== undefined) {
    vars.name = localizedName(vars.cardId)
  }
  if (typeof vars.kind === 'string') {
    const kindKey = KIND_KEYS[vars.kind]
    const kind = kindKey ? translated(kindKey) : undefined
    if (kind) vars.kind = kind
  }
  if (event.code === 'attackCancelled' && typeof vars.reason === 'string') {
    const reasonKey = ATTACK_CANCEL_KEYS[vars.reason]
    const reason = reasonKey ? translated(reasonKey) : undefined
    if (reason) vars.reason = reason
  }
  return translated(`duel.msg.${event.code}`, vars) ?? event.text
}
